import api from "../../../shared/api.jsx";

/**
 * Call Request Service - API calls for call request management
 * Follows the same pattern as other services in the codebase
 */

/**
 * Get all call requests with optional filters
 * @param {Object} filters - Filter parameters
 * @param {string} filters.status - Status filter (e.g., "requested", "assigned")
 * @param {string} filters.assigned - Assignment filter (e.g., "assigned", "unassigned")
 * @param {string} filters.search - Search term for customer ID
 * @returns {Promise<Object>} Call requests data
 */
export const getCallRequests = async (filters = {}) => {
  try {
    const params = {};

    // Handle status filtering - don't send "active" to backend
    if (
      filters.status &&
      filters.status !== "all" &&
      filters.status !== "active"
    ) {
      params.status = filters.status;
    }

    if (filters.assigned && filters.assigned !== "all") {
      params.assigned = filters.assigned;
    }

    if (filters.search) {
      params.search = filters.search;
    }

    // Use the original endpoint for now (temporarily)
    const response = await api.get("/call-requests", { params });
    return response;
  } catch (error) {
    console.error("Error fetching call requests:", error);
    return {
      success: false,
      error: error.message || "Failed to fetch call requests",
    };
  }
};

/**
 * Get call request statistics
 * @returns {Promise<Object>} Statistics data
 */
export const getCallRequestStats = async () => {
  try {
    const response = await api.get("/call-requests/stats");
    return response;
  } catch (error) {
    console.error("Error fetching call request stats:", error);
    return {
      success: false,
      error: error.message || "Failed to fetch call request stats",
    };
  }
};

/**
 * Update call request status
 * @param {string} callRequestId - Call request ID
 * @param {string} status - New status
 * @param {string} notes - Optional notes
 * @returns {Promise<Object>} Update response
 */
export const updateCallRequestStatus = async (
  callRequestId,
  status,
  notes = ""
) => {
  try {
    const response = await api.put(`/call-requests/${callRequestId}/status`, {
      status,
      notes,
    });
    // Return the full response to preserve success property
    return response;
  } catch (error) {
    console.error("Error updating call request status:", error);
    return {
      success: false,
      error: error.message || "Failed to update call request status",
    };
  }
};

/**
 * Assign call request to agent
 * @param {string} callRequestId - Call request ID
 * @param {string} agentId - Agent ID
 * @returns {Promise<Object>} Assignment response
 */
export const assignCallRequest = async (callRequestId, agentId) => {
  try {
    const response = await api.post(`/call-requests/${callRequestId}/assign`, {
      agentId,
    });
    // Return the full response to preserve success property
    return response;
  } catch (error) {
    console.error("Error assigning call request:", error);
    return {
      success: false,
      error: error.message || "Failed to assign call request",
    };
  }
};

/**
 * Create new call request
 * @param {Object} callRequestData - Call request data
 * @returns {Promise<Object>} Creation response
 */
export const createCallRequest = async (callRequestData) => {
  try {
    const response = await api.post("/call-requests", callRequestData);
    // Return the full response to preserve success property
    return response;
  } catch (error) {
    console.error("Error creating call request:", error);
    return {
      success: false,
      error: error.message || "Failed to create call request",
    };
  }
};

/**
 * Get call request by ID
 * @param {string} callRequestId - Call request ID
 * @returns {Promise<Object>} Call request data
 */
export const getCallRequestById = async (callRequestId) => {
  try {
    const response = await api.get(`/call-requests/${callRequestId}`);
    // Return the response data or success status
    return response.data || response;
  } catch (error) {
    console.error("Error fetching call request:", error);
    return {
      success: false,
      error: error.message || "Failed to fetch call request",
    };
  }
};

/**
 * Get call requests by quote ID
 * @param {string} quoteId - Quote ID
 * @returns {Promise<Object>} Call requests data
 */
export const getCallRequestsByQuoteId = async (quoteId) => {
  try {
    const response = await api.get(`/call-requests/quote/${quoteId}`);
    // Return the data array from the response
    return response.data || [];
  } catch (error) {
    console.error("Error fetching call requests by quote:", error);
    return [];
  }
};

/**
 * Get pending call requests count
 * @returns {Promise<Object>} Count data
 */
export const getPendingCallRequestsCount = async () => {
  try {
    const response = await api.get("/call-requests/pending-count");
    // Return the count from the response
    return response.count || 0;
  } catch (error) {
    console.error("Error fetching pending count:", error);
    return 0;
  }
};

/**
 * Update call request notes
 * @param {string} callRequestId - Call request ID
 * @param {string} notes - New notes
 * @returns {Promise<Object>} Update response
 */
export const updateCallRequestNotes = async (callRequestId, notes) => {
  try {
    const response = await api.put(`/call-requests/${callRequestId}/notes`, {
      notes,
    });
    // Return the full response to preserve success property
    return response;
  } catch (error) {
    console.error("Error updating call request notes:", error);
    return {
      success: false,
      error: error.message || "Failed to update call request notes",
    };
  }
};

/**
 * Get call request history
 * @param {string} callRequestId - Call request ID
 * @returns {Promise<Object>} History data
 */
export const getCallRequestHistory = async (callRequestId) => {
  try {
    const response = await api.get(`/call-requests/${callRequestId}/history`);
    // Return the full response to preserve success property
    return response;
  } catch (error) {
    console.error("Error fetching call request history:", error);
    return {
      success: false,
      error: error.message || "Failed to fetch call request history",
      data: [],
    };
  }
};

/**
 * Get complete call request data for a quote
 * @param {string} quoteId - Quote ID
 * @returns {Promise<Object>} Complete call request data
 */
export const getCallRequestDataForQuote = async (quoteId) => {
  try {
    const response = await getCallRequestsByQuoteId(quoteId);
    return response;
  } catch (error) {
    console.error("Error fetching call request data for quote:", error);
    return {
      success: false,
      error: error.message || "Failed to fetch call request data for quote",
      data: null,
    };
  }
};
